import { useEffect, useRef, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import {
  REACT_APP_LANGUAGE,
  AUTH_ADMIN_CLIENT_IDS,
  AUTH_ADMIN_REDIRECT_URIS,
  ENTRA_TENANT_ID,
  ENTRA_AUTHORIZE_BASE_URL,
  REACT_APP_COUNTRY,
} from '../envVars';
import {
  isUserAuthenticated,
  isLoggedInAuthenticated,
  getAmwayLoginUrl,
  getEnvironment,
  LoggerService,
} from '../ui/utils';
import { Header, Login } from '../ui/app-components';
import { AdminHome } from '../pages/admin/Admin';
import { Landing } from '../pages/Landing';
import { Footer } from '../ui/app-components/Footer';
import { PrivacyMain } from '../pages/privacy/Privacy';
import { TermsMain } from '../pages/terms/Terms';
import { AdminToast, ToastProvider } from '../ui/components';
import { getContent } from '../sdk/services/contentService';
import { Certificate } from '../pages/profile/Certificate';
import { AuthCallback } from '../pages/admin/AuthCallback';

const msalConfig = {
  auth: {
    clientId: AUTH_ADMIN_CLIENT_IDS,
    authority: `https://${ENTRA_AUTHORIZE_BASE_URL}/${ENTRA_TENANT_ID}`,
    redirectUri: AUTH_ADMIN_REDIRECT_URIS,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source');
  const teamParam = searchParams.get('t');
  const idParam = searchParams.get('id');
  const [isLogin, setIsLogin] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const isProd = useRef(['PROD'].includes(getEnvironment())).current;

  useEffect(() => {
    const locale = REACT_APP_LANGUAGE || 'en-my';
    const country = REACT_APP_COUNTRY || 'MY';
    localStorage.setItem('locale', locale);
    localStorage.setItem('country', country);
  }, []);

  useEffect(() => {
    const authenticated =
      isLogin === false ? isUserAuthenticated() && isLoggedInAuthenticated() : isLogin;

    setIsAuthenticated(authenticated);
  }, [isLogin]);

  useEffect(() => {
    const url = window.location.pathname;
    // `?id=` marks the promotions deep-link on the ROOT/landing path (it redirects to
    // /promotions). Scope the header-hide to that case only — real app routes like
    // /campaigns/.../user?id= (profile, view team member progress) also use ?id= and
    // must keep the header (logo + notification).
    const isPromotionDeepLink = !!idParam && (url === '/' || url === '');
    if (
      url.startsWith('/privacy-security') ||
      url.startsWith('/terms-and-conditions') ||
      url.startsWith('/certificate') ||
      url.startsWith('/promotions') ||
      isPromotionDeepLink
    ) {
      setShowHeader(false);
    } else if (url.startsWith('/admin')) {
      setIsAdmin(true);
      setShowHeader(true);
    } else {
      setShowHeader(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const shouldFetchConfig = location.pathname === '/login/redirect';

    if (!shouldFetchConfig) return;

    const fetchAndRedirect = async () => {
      try {
        const config = await getContent('user_login', 'userLogin', false);

        if (config) {
          const url = getAmwayLoginUrl({ source: sourceParam, teamParam: teamParam, id: idParam, postLoginUrl: idParam ? '/promotions' : null });
          window.location.replace(url);
        }
      } catch (error) {
        LoggerService.log('fetchPortalConfig: Failed to retrieve content data. Using default content.');

        const url = getAmwayLoginUrl({ source: sourceParam, teamParam: teamParam, id: idParam, postLoginUrl: idParam ? '/promotions' : null });
        window.location.replace(url);
      }
    };

    fetchAndRedirect();
  }, [location.pathname]);

  // Check if the current path is /login/redirect
  if (location.pathname === '/login/redirect') {
    return null; // Do not render anything
  }

  return (
    <MsalProvider instance={msalInstance}>
      {(isAuthenticated || isAdmin) && showHeader && <Header />}
      <Routes>
        {!isProd && (
          <>
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/login?source=email" element={<Login />} />
            <Route path="/auth/login?source=createTeam" element={<Login />} />
          </>
        )}
        <Route path="/admin/authorize" element={<AuthCallback />} />
        <Route path="/admin/*" element={<AdminHome />} />
        <Route
          path="/*"
          element={
            <ToastProvider>
              <AdminToast timeout={100000} topPosition="100px">
                <Landing setIsLogin={setIsLogin} />
              </AdminToast>
            </ToastProvider>
          }
        />
        <Route path="/privacy-security" element={<PrivacyMain />} />
        <Route path="/terms-and-conditions" element={<TermsMain />} />
        <Route path="/certificate" element={<Certificate />} />
      </Routes>
      <Footer campaignId={idParam}/>
    </MsalProvider>
  );
};

export default AppRoutes;
